import { Suspense } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { Email } from '@/domains/auth/features/Email/Email';
import { ForgotPassword } from '@/domains/auth/features/ForgotPassword/ForgotPassword';
import { ForgotUsername } from '@/domains/auth/features/ForgotUsername/ForgotUsername';
import { Otp } from '@/domains/auth/features/Otp/Otp';
import { SignIn } from '@/domains/auth/features/SignIn/SignIn';
import { UpdatePassword } from '@/domains/auth/features/UpdatePassword/UpdatePassword';
import { UserPermissions } from '@/domains/user/enums/UserPermissions';
import { App } from '@/App';
import { PageLoader } from '@/components/feedback/PageLoader/PageLoader';
import { AuthOrUsernameGuard } from '@/components/guards/AuthOrUsernameGuard/AuthOrUsernameGuard';
import { PermissionGuard } from '@/components/guards/PermissionGuard/PermissionGuard';
import { AuthProtectedPage } from '@/pages/AuthProtectedPage';
import { UnAuthProtectedPage } from '@/pages/UnAuthProtectedPage';
import {
  LazyAdminUsersPage,
  LazyClaimSetPage,
  LazyErrorPage,
  LazyMyClaimsPage,
  LazyMyTasksPage,
  LazyPartyDetailsPage,
  LazyProfilePage,
} from '@/routes/lazyRoutes';
import { DEFAULT_PATH, EditUserPaths, ProfileDetailsPaths, RootPaths } from '@/routes/paths';

export const router = createBrowserRouter([
  {
    element: <App />,
    errorElement: <LazyErrorPage />,
    path: RootPaths.ROOT,
    children: [
      { index: true, element: <Navigate to={DEFAULT_PATH} replace /> },
      {
        path: RootPaths.SIGN_IN,
        element: (
          <UnAuthProtectedPage>
            <SignIn />
          </UnAuthProtectedPage>
        ),
      },
      {
        path: RootPaths.OTP,
        element: (
          <UnAuthProtectedPage>
            <Otp />
          </UnAuthProtectedPage>
        ),
      },
      {
        path: RootPaths.FORGOT_PASSWORD,
        element: (
          <UnAuthProtectedPage>
            <ForgotPassword />
          </UnAuthProtectedPage>
        ),
      },
      {
        path: RootPaths.FORGOT_USERNAME,
        element: (
          <UnAuthProtectedPage>
            <ForgotUsername />
          </UnAuthProtectedPage>
        ),
      },
      {
        path: RootPaths.FORGOT_PASSWORD_EMAIL,
        element: (
          <UnAuthProtectedPage>
            <Email />
          </UnAuthProtectedPage>
        ),
      },
      {
        path: RootPaths.FORGOT_USERNAME_EMAIL,
        element: (
          <UnAuthProtectedPage>
            <Email />
          </UnAuthProtectedPage>
        ),
      },
      {
        path: RootPaths.UPDATE_PASSWORD,
        element: (
          <AuthOrUsernameGuard>
            <UpdatePassword />,
          </AuthOrUsernameGuard>
        ),
      },
      {
        path: `${RootPaths.TASKS}`,
        element: (
          <AuthProtectedPage>
            <LazyMyTasksPage />
          </AuthProtectedPage>
        ),
      },
      {
        path: `${RootPaths.CLAIMS}`,
        element: (
          <AuthProtectedPage>
            <LazyMyClaimsPage />
          </AuthProtectedPage>
        ),
      },
      {
        path: `${RootPaths.CLAIM_SET}`,
        element: (
          <AuthProtectedPage>
            <LazyClaimSetPage />
          </AuthProtectedPage>
        ),
      },
      {
        path: `${RootPaths.PARTY_DETAILS}`,
        element: (
          <AuthProtectedPage>
            <LazyPartyDetailsPage />
          </AuthProtectedPage>
        ),
      },
      {
        path: EditUserPaths.USERS,
        element: (
          <AuthProtectedPage>
            <PermissionGuard permissions={UserPermissions.ViewSystemTab} redirectTo={RootPaths.TASKS}>
              <Suspense fallback={<PageLoader />}>
                <LazyAdminUsersPage />
              </Suspense>
            </PermissionGuard>
          </AuthProtectedPage>
        ),
      },
      {
        path: ProfileDetailsPaths.PROFILE_DETAILS,
        element: (
          <AuthProtectedPage>
            <PermissionGuard permissions={UserPermissions.ViewProfile} redirectTo={RootPaths.TASKS}>
              <Suspense fallback={<PageLoader />}>
                <LazyProfilePage />
              </Suspense>
            </PermissionGuard>
          </AuthProtectedPage>
        ),
      },
    ],
  },
]);
